/**
 * CRM v2 — R3: aba "Reciclagem de leads" em Configurações › Comercial (CRM).
 *
 * Leads parados por X dias são liberados: voltam pra um pool aberto ou vão
 * direto pra outro vendedor (conforme o modo). Tudo persiste no SQLite via
 * /api/v1/crm/config/reciclagem — nada de estado no navegador.
 *
 * Padrão de salvamento (igual AbaAcesso/OrcamentoConfigPage): numéricos
 * salvam no blur/Enter; switch e select salvam no change. Toast em falha.
 */

import { useEffect, useState } from 'react';

import { Card, HelpTip, Input, Label, Select, SelectOption, Switch, useToast } from '@/components/ui';
import { cn } from '@/lib/cn';
import {
  normalizarInteiroNaoNegativo,
  useReciclagemConfig,
  useReciclagemConfigActions,
  type ReciclagemConfig,
} from '@/lib/crm/reciclagem-config-store';

export function ReciclagemConfigTab() {
  const configQuery = useReciclagemConfig();
  const { salvar } = useReciclagemConfigActions();
  const toast = useToast();
  const config = configQuery.data;

  const salvarCampo = async (body: Partial<ReciclagemConfig>) => {
    try {
      await salvar.mutateAsync(body);
    } catch {
      toast.show('Não foi possível salvar. Tente de novo.', { variant: 'error' });
    }
  };

  if (configQuery.isLoading) {
    return <p className="text-sm text-ink-3 dark:text-ink-dark-3">Carregando…</p>;
  }
  if (!config) {
    return <p className="text-sm text-ink-3 dark:text-ink-dark-3">Não foi possível carregar a configuração.</p>;
  }

  const desligada = !config.ativo;

  return (
    <div className="space-y-4 min-w-0">
      <Card variant="flat" className="p-4 min-w-0">
        <h3 className="text-base font-bold text-ink-1 dark:text-ink-dark-1 mb-1 inline-flex items-center gap-1.5">
          Reciclagem de leads
          <HelpTip texto="Rotina automática: lead parado demais é tirado do vendedor e volta pra circulação, pra ninguém deixar contato esfriar." />
        </h3>
        <p className="text-sm text-ink-3 dark:text-ink-dark-3 mb-3">
          Leads parados por muito tempo voltam pra um pool aberto ou são passados pra outro vendedor. Assim
          nenhum lead fica esquecido na mão de alguém.
        </p>
        <Switch
          label="Reciclagem ligada"
          hint={desligada ? 'Desligada: nenhum lead é liberado automaticamente.' : 'Ligada: os prazos abaixo valem pra todos os vendedores.'}
          checked={config.ativo}
          onChange={(e) => void salvarCampo({ ativo: e.target.checked })}
        />
      </Card>

      {/* U6: grid 2–3 colunas. Desligada = campos visíveis, mas desabilitados. */}
      <div className={cn('grid sm:grid-cols-2 lg:grid-cols-3 gap-4 items-start', desligada && 'opacity-60')}>
        {/* CA-11a: os prazos da reciclagem contam DIAS CORRIDOS (fim de semana
            e feriado incluídos) — o rótulo e o "?" deixam isso explícito. */}
        <CampoDias
          rotulo="Liberar lead Novo após (dias corridos)"
          ajuda="Lead com status Novo parado esse tempo é liberado pra outra pessoa."
          help="Dias corridos: contam fins de semana e feriados, não só dias úteis."
          valor={config.diasLiberarNovo}
          desabilitado={desligada}
          onSalvar={(n) => void salvarCampo({ diasLiberarNovo: n })}
        />
        <CampoDias
          rotulo="Liberar lead Em contato após (dias corridos)"
          ajuda="Lead com status Em contato parado esse tempo é liberado pra outra pessoa."
          help="Dias corridos: contam fins de semana e feriados, não só dias úteis."
          valor={config.diasLiberarEmContato}
          desabilitado={desligada}
          onSalvar={(n) => void salvarCampo({ diasLiberarEmContato: n })}
        />
        <CampoDias
          rotulo="Avisar o dono (dias corridos antes)"
          ajuda="O dono do lead recebe um aviso esse número de dias antes da liberação."
          help="O aviso chega antes da liberação, com tempo do vendedor agir e não perder o lead. Dias corridos: contam fins de semana e feriados."
          valor={config.avisoDias}
          desabilitado={desligada}
          onSalvar={(n) => void salvarCampo({ avisoDias: n })}
        />
        <Card variant="flat" className="p-4 min-w-0">
          <span className="flex items-center gap-1.5 mb-1.5">
            <Label htmlFor="reciclagem-modo" className="mb-0">Modo</Label>
            <HelpTip texto="Pool: o lead liberado fica numa lista aberta e qualquer vendedor pode pegar. Redistribuição: o sistema já entrega direto pro vendedor com menos leads." />
          </span>
          <Select
            id="reciclagem-modo"
            size="sm"
            value={config.modo}
            disabled={desligada}
            onChange={(e) => void salvarCampo({ modo: e.target.value as ReciclagemConfig['modo'] })}
            className="mt-1 w-full"
          >
            <SelectOption value="pool">Pool — qualquer vendedor pega</SelectOption>
            <SelectOption value="redistribuicao">Redistribuição automática — vai pro vendedor com menos leads</SelectOption>
          </Select>
          <p className="text-sm text-ink-3 dark:text-ink-dark-3 mt-1.5">O que acontece com o lead quando ele é liberado.</p>
          {config.modo === 'redistribuicao' && (
            <p className="text-sm text-ink-3 dark:text-ink-dark-3 mt-1.5 italic">
              No modo redistribuição não existe pool aberto — o lead já reaparece com o novo dono.
            </p>
          )}
        </Card>
        <CampoDias
          rotulo="Espera pra repegar o mesmo lead (dias corridos)"
          ajuda="Quem perdeu um lead espera esse tempo pra poder pegar o mesmo lead de novo."
          help="Período de espera que evita o mesmo vendedor segurar o lead de novo logo depois de perdê-lo por inatividade. Dias corridos: contam fins de semana e feriados."
          valor={config.cooldownDias}
          desabilitado={desligada}
          onSalvar={(n) => void salvarCampo({ cooldownDias: n })}
        />
        <CampoDias
          rotulo="Limite de leads por vendedor"
          ajuda="Quantos leads cada vendedor pode ter ao mesmo tempo. Use 0 pra deixar sem limite."
          help="Teto de leads simultâneos: vendedor no limite não recebe leads novos até liberar espaço. Com 0, não há limite."
          valor={config.tetoPorVendedor}
          desabilitado={desligada}
          onSalvar={(n) => void salvarCampo({ tetoPorVendedor: n })}
        />
      </div>
    </div>
  );
}

/**
 * Campo numérico (inteiro >= 0) que salva no blur/Enter. Valor inválido não
 * salva — o campo volta pro valor atual, sem toast (é só digitação).
 */
function CampoDias(props: {
  rotulo: string;
  ajuda: string;
  /** H1: explicação extra de leigo no "?" ao lado do rótulo (opcional). */
  help?: string;
  valor: number;
  desabilitado: boolean;
  onSalvar: (n: number) => void;
}) {
  const { rotulo, ajuda, help, valor, desabilitado, onSalvar } = props;
  const [texto, setTexto] = useState(String(valor));

  // Config mudou no servidor (outro campo salvo / refetch) → re-sincroniza.
  useEffect(() => {
    setTexto(String(valor));
  }, [valor]);

  const confirmar = () => {
    const n = normalizarInteiroNaoNegativo(texto);
    if (n === null) {
      setTexto(String(valor));
      return;
    }
    setTexto(String(n));
    if (n !== valor) onSalvar(n);
  };

  const inputId = `reciclagem-${rotulo.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

  return (
    <Card variant="flat" className="p-4 min-w-0">
      <span className="flex flex-wrap items-center gap-1.5 mb-1.5">
        <Label htmlFor={inputId} className="mb-0">{rotulo}</Label>
        {help && <HelpTip texto={help} />}
      </span>
      <Input
        id={inputId}
        size="sm"
        type="number"
        min={0}
        step={1}
        inputMode="numeric"
        value={texto}
        disabled={desabilitado}
        onChange={(e) => setTexto(e.target.value)}
        onBlur={confirmar}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            confirmar();
          } else if (e.key === 'Escape') {
            setTexto(String(valor));
          }
        }}
        className="mt-1 w-full"
      />
      <p className="text-sm text-ink-3 dark:text-ink-dark-3 mt-1.5">{ajuda}</p>
    </Card>
  );
}

export default ReciclagemConfigTab;
